import React from "react";
import { EditorDefns, EditorProps, isString } from "../../utility";
import { isObjectRecord } from "../typeGuards";
import { ArsTableCellContextMenuProvider } from "./ArsTableCellContextMenuProvider";
import { useArsTable } from "./ArsTableContextProvider";
import { ARST_PATCH_DATA, ArsTableCellData, ArsTableCellType } from "./interface";

export interface ArsTableCellProps<T = string> extends EditorProps<ArsTableCellData<T>> {
  rowId?: string;
  colId?: string;
}

/**
 * Cell renderer for ArsTable scaffold cells.
 * Resolves display versus annotation view at render time and dispatches cell updates.
 *
 * @param props - Scaffold editor props
 * @param props.id - Unique scaffold-generated cell identifier
 * @param props.colId - Column identifier passed by ScaffoldCell
 * @param props.rowId - Row identifier passed by ScaffoldCell
 * @param props.value - Dual-view scaffold cell value
 * @returns ArsTable cell content
 */
export const ArsTableCell = <T,>({
  id,
  colId,
  rowId,
  value,
}: ArsTableCellProps<T>): React.ReactElement => {
  const { state, dispatch } = useArsTable<T>();

  const editorProps = state?.settings.editorProps;
  const Editor = editorProps?.Editor;
  const isAnnotationsView = state?.settings.tlfViewMode.value === "ANNOTATIONS";

  const displayValue = value.displayContent;
  const annotationValue = value.annotationContent;

  return !Editor || !editorProps || !rowId || !colId ? (
    <></>
  ) : (
    <div style={{ width: "100%", height: "100%" }}>
      <ArsTableCellContextMenuProvider
        rowId={rowId}
        colId={colId}
        value={value}
      >
        <Editor
          id={id}
          value={
            isAnnotationsView
              ? editorProps.textToT(annotationValue)
              : (displayValue ??
                // istanbul ignore next
                editorProps.blankT)
          }
          setValue={(newValue) => {
            const updates = [
              {
                dataPath: `${value.dataPath}/label`,
                value: newValue,
              },
              {
                dataPath: `scaffoldModel/cellData/${rowId}/${colId}/displayContent`,
                value: newValue,
              },
            ];
            dispatch({
              operation: ARST_PATCH_DATA,
              updates,
            });
          }}
          editable={
            !isAnnotationsView &&
            !state.settings.disabled &&
            !value.disabled &&
            (("targetType" in value && value.targetType !== "dataDriven") ||
              value.cellType === ArsTableCellType.RowHeader)
          }
          textAlignment="left"
        />
      </ArsTableCellContextMenuProvider>
    </div>
  );
};

ArsTableCell.displayName = "ArsTableCell";

/**
 * Create editor props for use with ArsTableCell
 *
 * @param tProps - Editor definitions for the generic type T used in the cell's displayContent and annotationContent
 * @returns - Editor definitions for ArsTableCell with cell data as the generic type
 */
export const ArsTableCellEditorProps = <T,>(
  tProps: EditorDefns<T>,
): EditorDefns<ArsTableCellData<T>> => ({
  Editor: ArsTableCell,
  blankT: {
    annotationContent: "",
    cellType: ArsTableCellType.DataCell,
    columnId: "",
    columnIndex: -1,
    dataPath: "",
    displayContent: tProps.textToT(""),
    rowId: "",
  },
  getTextFromT: (cellValue: ArsTableCellData<T>) => {
    const cellDisplay = cellValue.displayContent;
    return tProps.getTextFromT(cellDisplay);
  },
  isT: (value: unknown): value is ArsTableCellData<T> => {
    return (
      isObjectRecord(value) &&
      "annotationContent" in value &&
      isString(value.annotationContent) &&
      "cellType" in value &&
      Object.values(ArsTableCellType).includes(value.cellType as ArsTableCellType) &&
      "dataPath" in value &&
      isString(value.dataPath) &&
      "displayContent" in value &&
      tProps.isT(value.displayContent)
    );
  },
  joinTintoBlock: (cellValues: ArsTableCellData<T>[]) => {
    const displayContents = cellValues.map((cv) => cv.displayContent);
    return {
      annotationContent: cellValues.map((cv) => cv.annotationContent).join("\n"),
      cellType: ArsTableCellType.DataCell,
      columnId:
        cellValues[0]?.columnId ??
        // istanbul ignore next
        "",
      columnIndex:
        cellValues[0]?.columnIndex ??
        // istanbul ignore next
        -1,
      dataPath:
        cellValues[0]?.dataPath ??
        // istanbul ignore next
        "",
      displayContent: tProps.joinTintoBlock(displayContents),
      rowId:
        cellValues[0]?.rowId ??
        // istanbul ignore next
        "",
    };
  },
  replaceTextInT: (
    cellValue: ArsTableCellData<T>,
    oldPhrase: string,
    newPhrase: ArsTableCellData<T>,
  ) => {
    return {
      ...cellValue,
      displayContent: tProps.replaceTextInT(
        cellValue.displayContent,
        oldPhrase,
        newPhrase.displayContent,
      ),
    };
  },
  splitTintoLines: (cellValue: ArsTableCellData<T>) => {
    const displayContent = cellValue.displayContent;
    const splitDisplay = tProps.splitTintoLines(displayContent);
    return splitDisplay.map((line) => ({
      annotationContent: cellValue.annotationContent,
      cellType: ArsTableCellType.DataCell,
      columnId: cellValue.columnId,
      columnIndex: cellValue.columnIndex,
      dataPath: cellValue.dataPath,
      displayContent: line,
      rowId: cellValue.rowId,
    }));
  },
  textToT: (text: string) => ({
    annotationContent: "",
    cellType: ArsTableCellType.DataCell,
    columnId: "",
    columnIndex: -1,
    dataPath: "",
    displayContent: tProps.textToT(text),
    rowId: "",
  }),
});
